import { useQuery } from '@tanstack/react-query'
import { getSupabaseClient } from '@/lib/supabase'
import type { Organization } from '@/lib/supabase'
import type { UserRole } from '@dcp/shared'

/**
 * Hook to fetch the organizations the user can access.
 *
 * The access rules are NOT re-implemented here (DCP-350). The RLS policy on
 * `public.organizations` is `USING (id IN (SELECT accessible_organization_ids()))`,
 * so a plain select already returns exactly what the caller may reach:
 *
 * - super_admin — every organization, suspended ones included;
 * - anyone else — their home organization plus every organization they hold a
 *   membership row for, filtered to active ones.
 *
 * This used to branch on the role and mirror those rules client-side, which is
 * how the multi-org case went wrong: the database learned about memberships
 * while the client kept returning "just your own organization". One source of
 * truth removes that whole class of bug — and the same select now covers a user
 * belonging to several organizations without a special case.
 *
 * @param userRole - The user's role. Not used for filtering any more; it keys
 *   the cache and gates the query until the profile has loaded.
 * @param userOrganizationId - The user's home organization id, part of the
 *   cache key so switching users cannot serve a stale list.
 * @returns Query result with accessible organizations, ordered by name
 */
export function useAccessibleOrganizations(
  userRole: UserRole | undefined,
  userOrganizationId: string | null | undefined
) {
  return useQuery({
    queryKey: ['accessible-organizations', userRole, userOrganizationId],
    queryFn: async (): Promise<Organization[]> => {
      const client = getSupabaseClient()

      const { data, error } = await client
        .from('organizations')
        .select('*')
        .order('name')

      if (error) {
        console.error('Error fetching accessible organizations:', error)
        throw error
      }

      return data || []
    },
    enabled: !!userRole,
  })
}
